import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { updateLocale } from "../api/settings";
import { useAuth } from "../auth/AuthContext";

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
  { code: "el", label: "EL" },
  { code: "uk", label: "UK" },
] as const;

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleChange(lng: string) {
    i18n.changeLanguage(lng);
    setOpen(false);
    if (user) {
      updateLocale(lng).catch(() => {});
    }
  }

  const current = (i18n.resolvedLanguage ?? "en").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Change language"
        className="text-sm font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1"
      >
        {current}
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-20 rounded-md border border-slate-200 bg-white py-1 shadow-lg z-50">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitem"
              onClick={() => handleChange(l.code)}
              className={`w-full text-left px-3 py-1.5 text-sm ${
                current === l.label
                  ? "font-semibold text-indigo-600"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
